import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { ICooperative } from "../../exports/interfaces";

interface CooperativeSelectionDrawerProps {
  open: boolean;
  onClose: () => void;
  cooperatives: ICooperative[];
  selectedCoopIds: Set<number>;
  setSelectedCoopIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  noticeTo: string;
}

const VALID_COOPERATIVE_STATUSES = ["Active", "Approved", "Merged"];

const CooperativeSelectionDrawer: React.FC<CooperativeSelectionDrawerProps> = ({
  open,
  onClose,
  cooperatives,
  selectedCoopIds,
  setSelectedCoopIds,
  noticeTo,
}) => {
  const [coopSearchQuery, setCoopSearchQuery] = useState("");

  // Group cooperatives by natureOfWork
  const cooperativesByNature = useMemo(() => {
    const grouped: Record<string, ICooperative[]> = {};
    for (const coop of cooperatives || []) {
      if (!VALID_COOPERATIVE_STATUSES.includes(coop.status)) continue;
      const key = coop.natureOfWork || "Other";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(coop);
    }
    return grouped;
  }, [cooperatives]);

  // Get filtered cooperatives based on noticeTo selection
  const drawerCooperatives = useMemo(() => {
    if (noticeTo === "Specific Cooperative" || noticeTo === "All Cooperative") {
      return cooperativesByNature;
    }
    // Filter by matching natureOfWork
    const filtered: Record<string, ICooperative[]> = {};
    for (const [nature, coops] of Object.entries(cooperativesByNature)) {
      if (nature === noticeTo) {
        filtered[nature] = coops;
      }
    }
    return filtered;
  }, [cooperativesByNature, noticeTo]);

  const handleSelectAll = () => {
    const allIds = Object.values(drawerCooperatives)
      .flat()
      .map((c) => c.id);
    setSelectedCoopIds(new Set(allIds));
  };

  const handleDeselectAll = () => {
    setSelectedCoopIds(new Set());
  };

  const handleToggle = (id: number) => {
    setSelectedCoopIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <>
      {open && <div className="notification-backdrop" onClick={onClose}></div>}
      <div
        className={`notification-drawer ${open ? "show" : ""}`}
        style={{ overflowY: "auto" }}
      >
        <div className="notification-header">
          <h5>Select Cooperatives</h5>
          <button type="button" className="btn p-0" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Search */}
        <div className="position-relative mb-3">
          <Search
            size={16}
            className="position-absolute"
            style={{
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#999",
            }}
          />
          <input
            type="text"
            className="form-control"
            placeholder="Search cooperative..."
            value={coopSearchQuery}
            onChange={(e) => setCoopSearchQuery(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </div>

        {/* Select All / Deselect All */}
        <div className="d-flex justify-content-between mb-3">
          <button
            type="button"
            className="btn btn-sm btn-badge-primary px-3"
            onClick={handleSelectAll}
          >
            Select All
          </button>
          <button
            type="button"
            className="btn btn-sm btn-badge-border px-3"
            onClick={handleDeselectAll}
          >
            Deselect All
          </button>
        </div>

        {/* Cooperatives grouped by natureOfWork */}
        <div className="notification-body">
          {Object.entries(drawerCooperatives).map(([nature, coops]) => {
            const filteredCoops = coops.filter((c) =>
              c.nameEnglish
                ?.toLowerCase()
                .includes(coopSearchQuery.toLowerCase()),
            );
            if (filteredCoops.length === 0) return null;
            return (
              <div key={nature} className="mb-3">
                <h6
                  className="mb-2 text-uppercase"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--primary-color)",
                    letterSpacing: "0.5px",
                  }}
                >
                  {nature}
                </h6>
                {filteredCoops.map((coop) => (
                  <label
                    key={coop.id}
                    className="d-flex align-items-center gap-2 mb-2"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCoopIds.has(coop.id)}
                      onChange={() => handleToggle(coop.id)}
                      style={{
                        width: 16,
                        height: 16,
                        accentColor: "var(--primary-color)",
                      }}
                    />
                    <span style={{ fontSize: 14 }}>{coop.nameEnglish}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>

        {/* Done button */}
        <div className="mt-3">
          <button
            type="button"
            className="btn btn-badge-primary w-100"
            onClick={onClose}
          >
            Done ({selectedCoopIds.size} selected)
          </button>
        </div>
      </div>
    </>
  );
};

export default CooperativeSelectionDrawer;
